import { memo } from 'react';
import { ChevronDownIcon } from '@utils/icons';
import { MatrixCell } from './MatrixCell';
import type { IAreaEditor } from './matrixCellPropsEqual';
import type { IProgressMatrix } from './progressMatrix';
import { LABEL_COL } from './MatrixColumnHeader';
import styles from './matrixTable.module.css';

/**
 * A floor's LABEL (label pane): the collapse-toggle button. Its cells render
 * separately in the body pane (FloorCells) — the panes split each logical row
 * in two, sharing the same absolute gridRow. Scalar props (not the floor
 * object) so the default shallow-compare memo survives matrix rebuilds.
 */
export const FloorLabel = memo(function FloorLabel({
  floorId, floorName, collapsed, onToggle, gridRow,
}: {
  floorId: string;
  floorName: string;
  collapsed: boolean;
  onToggle: (floorId: string) => void;
  /** Absolute grid row (windowed rendering: absoluteRowIndex + 1). */
  gridRow: number;
}) {
  return (
    <button
      type="button"
      className={styles.floorLabel}
      style={{ gridRow }}
      onClick={() => onToggle(floorId)}
      aria-expanded={!collapsed}
      data-row={floorId}
      data-col={LABEL_COL}
    >
      <span className={styles.caret} data-collapsed={collapsed}>
        <ChevronDownIcon size={16} />
      </span>
      {floorName}
    </button>
  );
});

/** A zone's LABEL (label pane); its cells are ZoneCells in the body pane. */
export const ZoneLabel = memo(function ZoneLabel({
  rowKey, zoneName, gridRow,
}: {
  /** Crosshair anchor: `${floorId}::${zoneId}`. */
  rowKey: string;
  zoneName: string;
  /** Absolute grid row (windowed rendering: absoluteRowIndex + 1). */
  gridRow: number;
}) {
  return (
    <div className={styles.zoneLabel} style={{ gridRow }} data-row={rowKey} data-col={LABEL_COL}>
      {zoneName}
    </div>
  );
});

/** A floor's rollup CELLS (body pane) — `variant="floor"` progress fills. */
export const FloorCells = memo(function FloorCells({
  floor, columns, colStart, gridRow,
}: {
  floor: IProgressMatrix['floors'][number];
  columns: IProgressMatrix['columns'];
  /** Absolute index of columns[0] in the full matrix (windowed rendering). */
  colStart: number;
  /** Absolute grid row (windowed rendering: absoluteRowIndex + 1). */
  gridRow: number;
}) {
  return (
    <>
      {columns.map((col, i) => (
        <MatrixCell
          key={col.taskId}
          cell={floor.cells[col.taskId] ?? null}
          variant="floor"
          rowKey={floor.floorId}
          colKey={col.taskId}
          gridColumn={colStart + i + 1}
          gridRow={gridRow}
        />
      ))}
    </>
  );
});

/** A zone's editable status CELLS (body pane). */
export const ZoneCells = memo(function ZoneCells({
  zone, columns, colStart, floorId, areaEditor, gridRow,
}: {
  zone: IProgressMatrix['floors'][number]['zones'][number];
  columns: IProgressMatrix['columns'];
  /** Absolute index of columns[0] in the full matrix (windowed rendering). */
  colStart: number;
  floorId: string;
  areaEditor?: IAreaEditor;
  /** Absolute grid row (windowed rendering: absoluteRowIndex + 1). */
  gridRow: number;
}) {
  const rowKey = `${floorId}::${zone.zoneId}`;
  return (
    <>
      {columns.map((col, i) => (
        <MatrixCell
          key={col.taskId}
          cell={zone.cells[col.taskId] ?? null}
          rowKey={rowKey}
          colKey={col.taskId}
          gridColumn={colStart + i + 1}
          gridRow={gridRow}
          // Group (Gen-1) columns are aggregates — not editable; task columns are.
          editable={!col.isGroup}
          zoneId={zone.zoneId}
          areaEditor={areaEditor}
        />
      ))}
    </>
  );
});
